/**
 * `/poste/acces` — who is taking the desk.
 *
 * Every user-facing string here is vertical-NEUTRAL, and has to be: this
 * screen runs before any session exists, so there is no `me.get` and no
 * `organization.type` to look up vocabulary from. `gateDevice.picker`
 * authenticates on the device token and returns a gate name and a list of
 * names — nothing that says whether this is a résidence or an office. Rather
 * than guess "gardien" over "agent d'accueil", it says neither. (Prose below
 * still says gardien where it means the `member.role` value.)
 *
 * Two steps on one screen: pick a gardien from the ones assigned to this
 * device's gate (`gateDevice.picker`, authenticated by the device token
 * alone — no session exists yet), then enter their PIN, which mints the
 * gardien session via the `start-shift` auth endpoint.
 *
 * The PIN is captured from `document`, not from a focused input. That is the
 * same rule the scan station follows and for the same reason: on a shared
 * machine with a hardware keypad or an HID scanner plugged in, anything that
 * depends on an element keeping focus fails the first time somebody taps
 * elsewhere. It also means the on-screen keypad and a physical keyboard are
 * the same code path rather than two.
 */
import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Building2, ChevronRight, Delete } from "lucide-react";

import { Button } from "@gately/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@gately/ui/dialog";

import {
  StationBrand,
  StationError,
  StationEyebrow,
  StationLead,
  StationPanel,
  StationScreen,
  StationTitle,
} from "~/components/station/station-screen";
import { orpc } from "~/lib/orpc";
import { isUnauthorizedError } from "~/lib/orpc-error";
import { clearDeviceToken } from "~/lib/station/device-token";
import {
  PIN_MAX_LENGTH,
  PIN_MIN_LENGTH,
  startShift,
} from "~/lib/station/shift";

export const Route = createFileRoute("/poste/acces")({
  component: AccessPage,
});

interface Gardien {
  memberId: string;
  name: string;
}

function AccessPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Gardien | null>(null);
  const [unpairOpen, setUnpairOpen] = useState(false);

  // The syndic configures the device AFTER pairing — binds the gate, assigns
  // gardiens, sets PINs — so this polls. Without it the screen sits on the
  // first (empty) answer until someone reloads the browser.
  const picker = useQuery({
    ...orpc.gateDevice.picker.queryOptions(),
    retry: (failureCount, error) =>
      !isUnauthorizedError(error) && failureCount < 3,
    refetchInterval: 10_000,
  });

  // UNAUTHORIZED here means the token is stale or the device was revoked from
  // the console. Drop it and start over rather than leaving a dead screen.
  useEffect(() => {
    if (!isUnauthorizedError(picker.error)) return;
    clearDeviceToken();
    void navigate({ to: "/poste/appairage", replace: true });
  }, [picker.error, navigate]);

  const unpair = () => {
    clearDeviceToken();
    void navigate({ to: "/poste/appairage", replace: true });
  };

  const gateName = picker.data?.gateName ?? null;
  // `picker` returns a `readonly []` for an unconfigured device and a mutable
  // array otherwise; widened here so the render path sees one shape.
  const gardiens: readonly Gardien[] = picker.data?.gardiens ?? [];

  return (
    <StationScreen>
      <StationBrand
        right={
          <Button
            variant="ghost"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => setUnpairOpen(true)}
          >
            Dissocier ce poste
          </Button>
        }
      />

      <div className="mt-8 flex items-center gap-2">
        <Building2 className="text-primary size-3.5" aria-hidden="true" />
        <StationEyebrow tone="brand">
          Portail · {gateName ?? "Non configuré"}
        </StationEyebrow>
      </div>

      {selected ? (
        <PinStep
          gardien={selected}
          onBack={() => setSelected(null)}
          onStarted={async (mustChangePin) => {
            // REMOVE, not invalidate. The gate in `/poste`'s `beforeLoad`
            // reads `shift.current` through `ensureQueryData`, which serves a
            // cached entry even when it is stale — so invalidating would
            // leave the gate deciding from the answer given BEFORE this
            // gardien signed in, and bounce the desk straight back here.
            // Dropping the cache outright is also the right thing on a shared
            // machine: a new session is a new person.
            queryClient.removeQueries();
            await navigate({
              to: mustChangePin ? "/poste/code" : "/poste",
              replace: true,
            });
          }}
        />
      ) : (
        <>
          <StationTitle>Qui prend le poste ?</StationTitle>
          <StationLead>
            Ce poste est partagé. Choisissez votre profil pour commencer le
            service.
          </StationLead>

          <div className="mt-7 space-y-2.5">
            {picker.isPending ? (
              <StationPanel>
                <p className="text-muted-foreground text-[0.9375rem]">
                  Chargement…
                </p>
              </StationPanel>
            ) : gardiens.length === 0 ? (
              <StationPanel>
                <p className="text-muted-foreground text-[0.9375rem] leading-relaxed">
                  {gateName
                    ? "Personne n'est encore affectée à ce portail. La liste se met à jour automatiquement."
                    : "Poste en attente de configuration (portail et personnel). La liste se met à jour automatiquement."}
                </p>
              </StationPanel>
            ) : (
              gardiens.map((g) => (
                <button
                  key={g.memberId}
                  type="button"
                  onClick={() => setSelected(g)}
                  className="hover:border-primary/40 hover:bg-primary/8 focus-visible:ring-ring/50 flex h-18 w-full items-center gap-3.5 rounded-xl border border-white/10 bg-white/[0.04] px-4 text-left transition-colors focus-visible:ring-[3px] focus-visible:outline-none"
                >
                  <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-[0.9375rem] font-semibold text-neutral-200">
                    {initials(g.name)}
                  </span>
                  <span className="flex-1 truncate text-base font-semibold text-white">
                    {g.name}
                  </span>
                  <ChevronRight
                    className="size-5 shrink-0 text-neutral-600"
                    aria-hidden="true"
                  />
                </button>
              ))
            )}
          </div>
        </>
      )}

      <Dialog open={unpairOpen} onOpenChange={setUnpairOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dissocier ce poste ?</DialogTitle>
            <DialogDescription>
              Ce navigateur oubliera son appareil de portail et devra être
              ré-enregistré depuis la console avant de pouvoir servir à nouveau.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnpairOpen(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={unpair}>
              Dissocier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </StationScreen>
  );
}

/** First letters of the first two words — "Karim Bennani" → "KB". */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

function PinStep({
  gardien,
  onBack,
  onStarted,
}: {
  gardien: Gardien;
  onBack: () => void;
  onStarted: (mustChangePin: boolean) => Promise<void>;
}) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    (value: string) => {
      if (value.length < PIN_MIN_LENGTH) return;
      setBusy(true);
      setError(null);
      void (async () => {
        const res = await startShift({
          memberId: gardien.memberId,
          pin: value,
        });
        if (!res.ok) {
          setBusy(false);
          setError(res.message);
          setPin("");
          return;
        }
        // Deliberately leaves `busy` set: the navigation that follows unmounts
        // this component, and re-enabling the keypad first would let a second
        // press fire another start-shift.
        await onStarted(res.mustChangePin);
      })();
    },
    [gardien.memberId, onStarted],
  );

  const press = useCallback(
    (digit: string) => {
      if (busy) return;
      setError(null);
      setPin((p) => (p.length >= PIN_MAX_LENGTH ? p : p + digit));
    },
    [busy],
  );

  const backspace = useCallback(() => {
    if (busy) return;
    setPin((p) => p.slice(0, -1));
  }, [busy]);

  // Physical keyboard, captured on `document` — see the file docblock.
  //
  // `pin` is a dependency so Enter can submit the current value directly.
  // Reading it out of a `setPin` updater instead would submit TWICE: React
  // double-invokes state updaters in development to surface exactly this kind
  // of impurity, and a second `start-shift` mints a second session. The
  // listener re-attaching on each keystroke costs nothing here — unlike the
  // scan station, a PIN pad has no burst in flight to lose.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key >= "0" && e.key <= "9") {
        press(e.key);
      } else if (e.key === "Backspace") {
        e.preventDefault();
        backspace();
      } else if (e.key === "Enter") {
        e.preventDefault();
        submit(pin);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [press, backspace, submit, pin]);

  return (
    <>
      <StationTitle>Bonjour {gardien.name}</StationTitle>
      <StationLead>Entrez votre code PIN pour prendre le service.</StationLead>

      {/* The PIN as filled/empty pips: legible across the desk, and it never
          renders the digits themselves on a screen a visitor can see. */}
      <div
        className="mt-8 flex items-center justify-center gap-3.5"
        aria-live="polite"
        aria-label={`${pin.length} chiffre(s) saisi(s)`}
      >
        {Array.from({ length: PIN_MAX_LENGTH }, (_, i) => (
          <span
            key={i}
            className={
              i < pin.length
                ? "bg-primary size-3.5 rounded-full"
                : "size-3.5 rounded-full bg-white/15"
            }
          />
        ))}
      </div>

      {error ? <StationError>{error}</StationError> : null}

      <div className="mt-8 grid grid-cols-3 gap-3">
        {KEYS.map((k) => (
          <KeypadButton key={k} onClick={() => press(k)} disabled={busy}>
            {k}
          </KeypadButton>
        ))}
        <KeypadButton onClick={backspace} disabled={busy} label="Effacer">
          <Delete className="size-6" aria-hidden="true" />
        </KeypadButton>
        <KeypadButton onClick={() => press("0")} disabled={busy}>
          0
        </KeypadButton>
        <KeypadButton
          onClick={() => submit(pin)}
          disabled={busy || pin.length < PIN_MIN_LENGTH}
          variant="primary"
          label="Prendre le service"
        >
          <ChevronRight className="size-7" aria-hidden="true" />
        </KeypadButton>
      </div>

      <div className="mt-auto pt-8">
        <Button
          variant="ghost"
          className="text-muted-foreground hover:text-foreground h-12 w-full"
          onClick={onBack}
          disabled={busy}
        >
          Changer de profil
        </Button>
      </div>
    </>
  );
}

function KeypadButton({
  children,
  onClick,
  disabled,
  variant = "default",
  label,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  variant?: "default" | "primary";
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={
        // h-18 keeps every target well past the 44px minimum, on a screen
        // people hit with a thumb while holding a clipboard.
        "focus-visible:ring-ring/50 active:ease-out-quart flex h-18 items-center justify-center rounded-xl text-2xl font-medium transition-all select-none focus-visible:ring-[3px] focus-visible:outline-none active:scale-97 active:duration-(--duration-press) disabled:pointer-events-none disabled:opacity-40 " +
        (variant === "primary"
          ? "bg-primary text-primary-foreground hover:bg-primary/90"
          : "border border-white/10 bg-white/[0.06] text-white hover:bg-white/[0.12]")
      }
    >
      {children}
    </button>
  );
}
